import { Button, Divider, Input, InputNumber, Radio, Select, Space } from "antd/es";
import { useEffect, useState } from "react";
import { KDatePicker } from "../date/date";
import { BooleanFilterTypes, DateFilterTypes, NumberFilterTypes, StringFilterTypes, ValueType } from "./base-table";
import TriStateSwitch from "./tri-state-switch";

const { Option } = Select;

declare let window: any;

interface CustomFilterDropdownProps {
  selectedKeys: any;
  setSelectedKeys: any;
  confirm: any;
  clearFilters: any;
  dataType: any;
  visible: any;
  initialFilters: any;
  columnId: any;
  tableStyle?: any;
  dateSearchFormat?: string;
}

const CustomFilterDropdown = ({
  selectedKeys,
  setSelectedKeys,
  confirm,
  clearFilters,
  dataType,
  visible,
  initialFilters,
  columnId,
  tableStyle,
  dateSearchFormat
}: CustomFilterDropdownProps) => {
  const [searchType, setSearchType] = useState<
    StringFilterTypes | NumberFilterTypes | DateFilterTypes | BooleanFilterTypes
  >(
    dataType === ValueType.String
      ? StringFilterTypes.Contains
      : ValueType.Number
      ? NumberFilterTypes.Equals
      : DateFilterTypes.Equals
  );
  const [searchValue, setSearchValue] = useState<string>(selectedKeys?.value);
  const [searchValue2, setSearchValue2] = useState<string>(selectedKeys?.value2);
  const [logicalOperator, setLogicalOperator] = useState<"AND" | "OR">(selectedKeys?.logicalOperator || "AND");

  const [secondSearchType, setSecondSearchType] = useState<
    StringFilterTypes | NumberFilterTypes | DateFilterTypes | BooleanFilterTypes
  >(selectedKeys?.secondType);
  const [secondSearchValue, setSecondSearchValue] = useState<string>(selectedKeys?.secondValue || "");
  const [secondSearchValue2, setSecondSearchValue2] = useState<string>(selectedKeys?.secondValue2 || "");
  const [showSecondFilter, setShowSecondFilter] = useState<boolean>(
    Boolean(selectedKeys?.secondValue || selectedKeys?.secondType)
  );

  const language = localStorage.getItem("ml");

  const isString = (value: StringFilterTypes | NumberFilterTypes | DateFilterTypes | BooleanFilterTypes) => {
    if (
      value === StringFilterTypes.Contains ||
      value === StringFilterTypes.DoesNotContain ||
      value === StringFilterTypes.Equals ||
      value === StringFilterTypes.DoesNotEqual ||
      value === StringFilterTypes.BeginsWith ||
      value === StringFilterTypes.EndsWith ||
      value === StringFilterTypes.Blank ||
      value === StringFilterTypes.NotBlank
    ) {
      return true;
    }
    return false;
  };

  const isNumber = (value: NumberFilterTypes | StringFilterTypes | DateFilterTypes | BooleanFilterTypes) => {
    if (
      value === NumberFilterTypes.Equals ||
      value === NumberFilterTypes.DoesNotEqual ||
      value === NumberFilterTypes.GreaterThan ||
      value === NumberFilterTypes.GreaterThanOrEqual ||
      value === NumberFilterTypes.LessThan ||
      value === NumberFilterTypes.LessThanOrEqual ||
      value === NumberFilterTypes.Blank ||
      value === NumberFilterTypes.NotBlank ||
      value === NumberFilterTypes.Between
    ) {
      return true;
    }
    return false;
  };

  const isDate = (value: DateFilterTypes | StringFilterTypes | NumberFilterTypes | BooleanFilterTypes) => {
    if (
      value === DateFilterTypes.After ||
      value === DateFilterTypes.Before ||
      value === DateFilterTypes.Between ||
      value === DateFilterTypes.Equals ||
      value === DateFilterTypes.DoesNotEqual ||
      value === DateFilterTypes.Blank ||
      value === DateFilterTypes.NotBlank
    ) {
      return true;
    }
    return false;
  };

  const isBoolean = (value: StringFilterTypes | NumberFilterTypes | DateFilterTypes | BooleanFilterTypes) => {
    if (
      value === BooleanFilterTypes.Equals ||
      value === BooleanFilterTypes.DoesNotEqual ||
      value === BooleanFilterTypes.Blank ||
      value === BooleanFilterTypes.NotBlank
    ) {
      return true;
    }
    if (dataType === ValueType.Boolean) {
      return true;
    }
    return false;
  };

  const isBlankType = (value: StringFilterTypes | NumberFilterTypes | DateFilterTypes | BooleanFilterTypes) => {
    return (
      value === StringFilterTypes.Blank ||
      value === NumberFilterTypes.Blank ||
      value === DateFilterTypes.Blank ||
      value === BooleanFilterTypes.Blank ||
      value === StringFilterTypes.NotBlank ||
      value === NumberFilterTypes.NotBlank ||
      value === DateFilterTypes.NotBlank ||
      value === BooleanFilterTypes.NotBlank
    );
  };

  useEffect(() => {
    if (searchValue && searchValue.toString().trim() !== "") {
      setShowSecondFilter(true);
    }
  }, [searchValue]);

  useEffect(() => {
    if (isBlankType(searchType)) {
      setShowSecondFilter(true);
    }
  }, [searchType]);

  useEffect(() => {
    if (visible) {
      const searchType = findDataType(dataType);
      const currentType = selectedKeys?.type ?? searchType;
      setSearchType(currentType);
      setSelectedKeys({
        ...selectedKeys,
        type: currentType,
        columnId,
        logicalOperator: selectedKeys?.logicalOperator || logicalOperator,
        secondType: selectedKeys?.secondType,
        secondValue: selectedKeys?.secondValue,
        secondValue2: selectedKeys?.secondValue2
      });

      if (selectedKeys?.secondValue || selectedKeys?.secondType) {
        setShowSecondFilter(true);
        setSecondSearchType(selectedKeys?.secondType || searchType);
        setSecondSearchValue(selectedKeys?.secondValue || "");
        setSecondSearchValue2(selectedKeys?.secondValue2 || "");
      }
    }
  }, [visible]);

  useEffect(() => {
    if (initialFilters && initialFilters.value && initialFilters.type && initialFilters.value !== searchValue) {
      const searchType = findDataType(dataType);
      setSearchType(searchType);
      setSelectedKeys({
        ...selectedKeys,
        value: initialFilters.value,
        type: initialFilters.type,
        columnId,
        logicalOperator: initialFilters.logicalOperator || logicalOperator,
        secondType: initialFilters.secondType,
        secondValue: initialFilters.secondValue,
        secondValue2: initialFilters.secondValue2
      });
      setSearchValue(initialFilters.value);

      if (initialFilters.secondValue || initialFilters.secondType) {
        setShowSecondFilter(true);
        setSecondSearchType(initialFilters.secondType || searchType);
        setSecondSearchValue(initialFilters.secondValue || "");
        setSecondSearchValue2(initialFilters.secondValue2 || "");
      }

      if (initialFilters.logicalOperator) {
        setLogicalOperator(initialFilters.logicalOperator);
      }
      confirm();
    }
  }, []);

  const findDataType = (value: string) => {
    if (value === ValueType.Boolean) {
      return BooleanFilterTypes.Equals;
    }
    if (value === ValueType.String) {
      return StringFilterTypes.Contains;
    }
    if (value === ValueType.Number) {
      return NumberFilterTypes.Equals;
    }
    if (value === ValueType.Date) {
      return DateFilterTypes.Equals;
    }
    return StringFilterTypes.Contains;
  };

  const handleSearch = () => {
    const fullFilter = {
      value: searchValue,
      value2: searchValue2,
      type: searchType,
      columnId,
      logicalOperator,
      secondType: secondSearchType,
      secondValue: secondSearchValue,
      secondValue2: secondSearchValue2
    };

    setSelectedKeys(fullFilter);
    confirm();
  };

  const handleReset = () => {
    setSelectedKeys("");
    const searchType = findDataType(dataType);
    setSearchType(searchType);
    setSecondSearchType(searchType);
    setSearchValue("");
    setSecondSearchValue("");
    setSecondSearchValue2("");
    setShowSecondFilter(false);
    clearFilters();
    confirm();
  };

  const handleSearchValueChange = (e, secondValue?: boolean) => {
    if (secondValue) {
      const value2 = e && e.target === undefined ? e : e.target ? e.target.value : e;
      setSearchValue2(value2);
      setSelectedKeys({
        value: searchValue,
        value2,
        type: searchType,
        columnId,
        logicalOperator,
        secondType: secondSearchType,
        secondValue: secondSearchValue,
        secondValue2: secondSearchValue2
      });
      return;
    }

    const value = e && e.target === undefined ? e : e.target ? e.target.value : e;
    setSearchValue(value);

    if (value && value.toString().trim() !== "") {
      setShowSecondFilter(true);
    }

    setSelectedKeys({
      value,
      value2: searchValue2,
      type: searchType,
      columnId,
      logicalOperator,
      secondType: secondSearchType,
      secondValue: secondSearchValue,
      secondValue2: secondSearchValue2
    });
  };

  const handleSecondSearchValueChange = (e, secondValue?: boolean) => {
    if (secondValue) {
      const value2 = e && e.target === undefined ? e : e.target ? e.target.value : e;
      setSecondSearchValue2(value2);
      setSelectedKeys({
        value: searchValue,
        value2: searchValue2,
        type: searchType,
        columnId,
        logicalOperator,
        secondType: secondSearchType,
        secondValue: secondSearchValue,
        secondValue2: value2
      });
      return;
    }

    const value = e && e.target === undefined ? e : e.target ? e.target.value : e;
    setSecondSearchValue(value);
    setSelectedKeys({
      value: searchValue,
      value2: searchValue2,
      type: searchType,
      columnId,
      logicalOperator,
      secondType: secondSearchType,
      secondValue: value,
      secondValue2: secondSearchValue2
    });
  };

  const handleLogicalOperatorChange = (e) => {
    const newOperator = e.target.value;
    setLogicalOperator(newOperator);
    setSelectedKeys({
      ...selectedKeys,
      value: searchValue,
      value2: searchValue2,
      type: searchType,
      columnId,
      logicalOperator: newOperator,
      secondType: secondSearchType,
      secondValue: secondSearchValue,
      secondValue2: secondSearchValue2
    });
  };

  const isBlank =
    searchType === StringFilterTypes.Blank ||
    searchType === NumberFilterTypes.Blank ||
    searchType === DateFilterTypes.Blank ||
    searchType === BooleanFilterTypes.Blank;
  const isNotBlank =
    searchType === StringFilterTypes.NotBlank ||
    searchType === NumberFilterTypes.NotBlank ||
    searchType === DateFilterTypes.NotBlank ||
    searchType === BooleanFilterTypes.NotBlank;

  const isSecondBlank =
    secondSearchType === StringFilterTypes.Blank ||
    secondSearchType === NumberFilterTypes.Blank ||
    secondSearchType === DateFilterTypes.Blank ||
    secondSearchType === BooleanFilterTypes.Blank;
  const isSecondNotBlank =
    secondSearchType === StringFilterTypes.NotBlank ||
    secondSearchType === NumberFilterTypes.NotBlank ||
    secondSearchType === DateFilterTypes.NotBlank ||
    secondSearchType === BooleanFilterTypes.NotBlank;

  const isStringDataType = dataType === ValueType.String || !dataType;
  const isDesignTime = window.kuika.isDesignTime;

  const getContrastColor = (bgColor: string): string => {
    if (!bgColor) return "#000000";

    const color = bgColor.replace("#", "");

    const r = parseInt(color.substr(0, 2), 16);
    const g = parseInt(color.substr(2, 2), 16);
    const b = parseInt(color.substr(4, 2), 16);

    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    return luminance > 0.5 ? "#000000" : "#ffffff";
  };

  const tableBgColor = tableStyle?.backgroundColor || tableStyle?.background;
  const hasTableBgColor = tableBgColor && tableBgColor !== "transparent";
  const contrastColor = hasTableBgColor ? getContrastColor(tableBgColor) : "#000000";

  const uniqueWrapperClass = `kuika-filter-dropdown-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div
      className={uniqueWrapperClass}
      style={{ padding: 8, backgroundColor: tableStyle?.backgroundColor || tableStyle?.background || "#ffffff" }}
    >
      {hasTableBgColor && (
        <style>
          {`
            .${uniqueWrapperClass} .ant-input {
              background-color: transparent !important;
              border-color: ${contrastColor} !important;
              color: ${contrastColor} !important;
            }
            .${uniqueWrapperClass} .ant-input::placeholder {
              color: ${contrastColor}80 !important;
            }
            .${uniqueWrapperClass} .ant-select {
              background-color: transparent !important;
            }
            .${uniqueWrapperClass} .ant-select-selector {
              background-color: transparent !important;
              border-color: ${contrastColor} !important;
              color: ${contrastColor} !important;
            }
            .${uniqueWrapperClass} .ant-select-selection-item {
              color: ${contrastColor} !important;
            }
            .${uniqueWrapperClass} .ant-select-arrow {
              color: ${contrastColor} !important;
            }
            .${uniqueWrapperClass} .ant-input-number {
              background-color: transparent !important;
              border-color: ${contrastColor} !important;
            }
            .${uniqueWrapperClass} .ant-input-number-input {
              background-color: transparent !important;
              color: ${contrastColor} !important;
            }
            .${uniqueWrapperClass} .ant-radio-wrapper span {
              color: ${contrastColor} !important;
            }
            .${uniqueWrapperClass} .ant-radio-inner {
              border-color: ${contrastColor} !important;
              background-color: transparent !important;
            }
            .${uniqueWrapperClass} .ant-radio-checked .ant-radio-inner {
              background-color: ${contrastColor} !important;
              border-color: ${contrastColor} !important;
            }
            .${uniqueWrapperClass} .ant-radio-checked .ant-radio-inner::after {
              background-color: ${tableBgColor} !important;
            }
            .${uniqueWrapperClass} .ant-btn {
              background-color: transparent !important;
              border-color: ${contrastColor} !important;
              color: ${contrastColor} !important;
            }
            .${uniqueWrapperClass} .ant-btn:hover, .${uniqueWrapperClass} .ant-btn:focus {
              background-color: ${contrastColor}20 !important;
              border-color: ${contrastColor} !important;
              color: ${contrastColor} !important;
            }
            .${uniqueWrapperClass} .ant-btn-primary {
              background-color: ${contrastColor} !important;
              border-color: ${contrastColor} !important;
              color: ${tableBgColor} !important;
            }
            .${uniqueWrapperClass} .ant-btn-primary:hover, .${uniqueWrapperClass} .ant-btn-primary:focus {
              background-color: ${contrastColor}CC !important;
              border-color: ${contrastColor}CC !important;
              color: ${tableBgColor} !important;
            }
            .${uniqueWrapperClass} .ant-divider {
              border-color: ${contrastColor}40 !important;
            }
          `}
        </style>
      )}
      <Select
        value={searchType}
        style={{ width: "100%", marginBottom: 8, display: "block" }}
        onChange={(value) => {
          setSearchType(value);
          setSelectedKeys({
            value: searchValue,
            value2: searchValue2,
            type: value,
            columnId,
            logicalOperator,
            secondType: secondSearchType,
            secondValue: secondSearchValue,
            secondValue2: secondSearchValue2
          });
        }}
      >
        {isStringDataType && (
          <Option value={StringFilterTypes.Contains}>{language === "tr_TR" ? "İçerir" : "Contains"}</Option>
        )}
        {isStringDataType && (
          <Option value={StringFilterTypes.DoesNotContain}>
            {language === "tr_TR" ? "İçermez" : "Does Not Contain"}
          </Option>
        )}
        {isStringDataType && (
          <Option value={StringFilterTypes.Equals}>{language === "tr_TR" ? "Eşittir" : "Equals"}</Option>
        )}
        {isStringDataType && (
          <Option value={StringFilterTypes.DoesNotEqual}>
            {language === "tr_TR" ? "Eşit Değildir" : "Does Not Equal"}
          </Option>
        )}
        {isStringDataType && (
          <Option value={StringFilterTypes.BeginsWith}>{language === "tr_TR" ? "Başlar" : "Begins With"}</Option>
        )}
        {isStringDataType && (
          <Option value={StringFilterTypes.EndsWith}>{language === "tr_TR" ? "Biter" : "Ends With"}</Option>
        )}
        {isStringDataType && <Option value={StringFilterTypes.Blank}>{language === "tr_TR" ? "Boş" : "Blank"}</Option>}
        {isStringDataType && (
          <Option value={StringFilterTypes.NotBlank}>{language === "tr_TR" ? "Boş Değildir" : "Not Blank"}</Option>
        )}
        {dataType === ValueType.Number && (
          <Option value={NumberFilterTypes.Equals}>{language === "tr_TR" ? "Eşittir" : "Equals"}</Option>
        )}
        {dataType === ValueType.Number && (
          <Option value={NumberFilterTypes.DoesNotEqual}>
            {language === "tr_TR" ? "Eşit Değildir" : "Does Not Equal"}
          </Option>
        )}
        {dataType === ValueType.Number && (
          <Option value={NumberFilterTypes.GreaterThan}>{language === "tr_TR" ? "Büyüktür" : "Greater Than"}</Option>
        )}
        {dataType === ValueType.Number && (
          <Option value={NumberFilterTypes.GreaterThanOrEqual}>
            {language === "tr_TR" ? "Büyük Eşittir" : "Greater Than or Equal"}
          </Option>
        )}
        {dataType === ValueType.Number && (
          <Option value={NumberFilterTypes.LessThan}>{language === "tr_TR" ? "Küçüktür" : "Less Than"}</Option>
        )}
        {dataType === ValueType.Number && (
          <Option value={NumberFilterTypes.LessThanOrEqual}>
            {language === "tr_TR" ? "Küçük Eşittir" : "Less Than or Equal"}
          </Option>
        )}
        {dataType === ValueType.Number && (
          <Option value={NumberFilterTypes.Blank}>{language === "tr_TR" ? "Boş" : "Blank"}</Option>
        )}
        {dataType === ValueType.Number && (
          <Option value={NumberFilterTypes.NotBlank}>{language === "tr_TR" ? "Boş Değildir" : "Not Blank"}</Option>
        )}
        {dataType === ValueType.Date && (
          <Option value={DateFilterTypes.After}>{language === "tr_TR" ? "Tarihinden Sonra" : "After Date"}</Option>
        )}
        {dataType === ValueType.Date && (
          <Option value={DateFilterTypes.Before}>{language === "tr_TR" ? "Tarihinden Önce" : "Before Date"}</Option>
        )}
        {dataType === ValueType.Date && (
          <Option value={DateFilterTypes.Between}>
            {language === "tr_TR" ? "Tarihleri Arasında" : "Between Dates"}
          </Option>
        )}
        {dataType === ValueType.Date && (
          <Option value={DateFilterTypes.Equals}>{language === "tr_TR" ? "Eşittir" : "Equals"}</Option>
        )}
        {dataType === ValueType.Date && (
          <Option value={DateFilterTypes.DoesNotEqual}>
            {language === "tr_TR" ? "Eşit Değildir" : "Does Not Equal"}
          </Option>
        )}
        {dataType === ValueType.Date && (
          <Option value={DateFilterTypes.Blank}>{language === "tr_TR" ? "Boş" : "Blank"}</Option>
        )}
        {dataType === ValueType.Date && (
          <Option value={DateFilterTypes.NotBlank}>{language === "tr_TR" ? "Boş Değildir" : "Not Blank"}</Option>
        )}
        {dataType === ValueType.Boolean && (
          <Option value={BooleanFilterTypes.Equals}>{language === "tr_TR" ? "Eşittir" : "Equals"}</Option>
        )}
        {dataType === ValueType.Boolean && (
          <Option value={BooleanFilterTypes.DoesNotEqual}>
            {language === "tr_TR" ? "Eşit Değildir" : "Does Not Equal"}
          </Option>
        )}
        {dataType === ValueType.Boolean && (
          <Option value={BooleanFilterTypes.Blank}>{language === "tr_TR" ? "Boş" : "Blank"}</Option>
        )}
        {dataType === ValueType.Boolean && (
          <Option value={BooleanFilterTypes.NotBlank}>{language === "tr_TR" ? "Boş Değildir" : "Not Blank"}</Option>
        )}
      </Select>

      {isString(searchType) && !isBlank && !isNotBlank && (
        <Input
          placeholder={language === "tr_TR" ? "Filtrele..." : "Filter..."}
          value={searchValue}
          onChange={(e) => handleSearchValueChange(e)}
          style={{ width: "100%", marginBottom: 8, display: "block" }}
          autoFocus
          readOnly={isDesignTime}
        />
      )}
      {isNumber(searchType) && !isBlank && !isNotBlank && (
        <InputNumber
          placeholder={language === "tr_TR" ? "Filtrele..." : "Filter..."}
          value={searchValue}
          onChange={(e) => handleSearchValueChange(e)}
          style={{ width: "100%", marginBottom: 8, display: "block" }}
          autoFocus
          readOnly={isDesignTime}
        />
      )}
      {isDate(searchType) && !isBlank && !isNotBlank && (
        <div style={{ width: "100%", marginBottom: 8 }}>
          <KDatePicker
            format={dateSearchFormat}
            displayType={
              (typeof searchValue === "string" && (searchValue.includes(":") || searchValue.includes("T"))) ||
              (dateSearchFormat &&
                (dateSearchFormat.includes("T") ||
                  dateSearchFormat.includes("HH:mm:ss") ||
                  dateSearchFormat.includes("HH:mm") ||
                  dateSearchFormat.includes("mm:ss")))
                ? ("datetime" as any)
                : ("date" as any)
            }
            placeholder={language === "tr_TR" ? "Filtrele..." : "Filter..."}
            value={searchValue}
            isTableSearch
            onChange={(val) => handleSearchValueChange(val)}
            disabled={isDesignTime}
            returnDisplayValue
          />
        </div>
      )}
      {searchType === NumberFilterTypes.Between && (
        <InputNumber
          placeholder={language === "tr_TR" ? "İkinci değer..." : "Second value..."}
          value={searchValue2}
          onChange={(e) => handleSearchValueChange(e, true)}
          style={{ width: "100%", marginBottom: 8, display: "block" }}
          readOnly={isDesignTime}
        />
      )}
      {searchType === DateFilterTypes.Between && (
        <div style={{ width: "100%", marginBottom: 8 }}>
          <KDatePicker
            format={dateSearchFormat}
            displayType={
              (typeof searchValue2 === "string" && (searchValue2.includes(":") || searchValue2.includes("T"))) ||
              (dateSearchFormat &&
                (dateSearchFormat.includes("T") ||
                  dateSearchFormat.includes("HH:mm:ss") ||
                  dateSearchFormat.includes("HH:mm") ||
                  dateSearchFormat.includes("mm:ss")))
                ? ("datetime" as any)
                : ("date" as any)
            }
            placeholder={language === "tr_TR" ? "Filtrele..." : "Filter..."}
            value={searchValue2}
            isTableSearch
            onChange={(val) => handleSearchValueChange(val, true)}
            returnDisplayValue
          />
        </div>
      )}
      {isBoolean(searchType) && !isBlank && !isNotBlank && (
        <TriStateSwitch
          value={searchValue || null}
          onChange={(value) => {
            setSearchValue(value);
            setSelectedKeys({
              value,
              type: searchType,
              columnId,
              logicalOperator,
              secondType: secondSearchType,
              secondValue: secondSearchValue,
              secondValue2: secondSearchValue2
            });
          }}
          language={language}
          source={"FilterDropdown"}
        />
      )}

      <Radio.Group
        value={logicalOperator}
        onChange={handleLogicalOperatorChange}
        style={{ width: "100%", marginBottom: 8, display: "flex", justifyContent: "center" }}
      >
        <Radio value="AND" style={{ display: "flex", alignItems: "center" }}>
          <span style={{ marginLeft: 8 }}>AND</span>
        </Radio>
        <Radio value="OR" style={{ display: "flex", alignItems: "center" }}>
          <span style={{ marginLeft: 8 }}>OR</span>
        </Radio>
      </Radio.Group>

      {showSecondFilter && (
        <>
          <Divider style={{ margin: "8px 0" }} />

          <Select
            value={secondSearchType}
            style={{ width: "100%", marginBottom: 8, display: "block" }}
            onChange={(value) => {
              setSecondSearchType(value);
              setSelectedKeys({
                value: searchValue,
                value2: searchValue2,
                type: searchType,
                columnId,
                logicalOperator,
                secondType: value,
                secondValue: secondSearchValue,
                secondValue2: secondSearchValue2
              });
            }}
          >
            {isStringDataType && (
              <Option value={StringFilterTypes.Contains}>{language === "tr_TR" ? "İçerir" : "Contains"}</Option>
            )}
            {isStringDataType && (
              <Option value={StringFilterTypes.DoesNotContain}>
                {language === "tr_TR" ? "İçermez" : "Does Not Contain"}
              </Option>
            )}
            {isStringDataType && (
              <Option value={StringFilterTypes.Equals}>{language === "tr_TR" ? "Eşittir" : "Equals"}</Option>
            )}
            {isStringDataType && (
              <Option value={StringFilterTypes.DoesNotEqual}>
                {language === "tr_TR" ? "Eşit Değildir" : "Does Not Equal"}
              </Option>
            )}
            {isStringDataType && (
              <Option value={StringFilterTypes.BeginsWith}>{language === "tr_TR" ? "Başlar" : "Begins With"}</Option>
            )}
            {isStringDataType && (
              <Option value={StringFilterTypes.EndsWith}>{language === "tr_TR" ? "Biter" : "Ends With"}</Option>
            )}
            {isStringDataType && (
              <Option value={StringFilterTypes.Blank}>{language === "tr_TR" ? "Boş" : "Blank"}</Option>
            )}
            {isStringDataType && (
              <Option value={StringFilterTypes.NotBlank}>{language === "tr_TR" ? "Boş Değildir" : "Not Blank"}</Option>
            )}
            {dataType === ValueType.Number && (
              <Option value={NumberFilterTypes.Equals}>{language === "tr_TR" ? "Eşittir" : "Equals"}</Option>
            )}
            {dataType === ValueType.Number && (
              <Option value={NumberFilterTypes.DoesNotEqual}>
                {language === "tr_TR" ? "Eşit Değildir" : "Does Not Equal"}
              </Option>
            )}
            {dataType === ValueType.Number && (
              <Option value={NumberFilterTypes.GreaterThan}>
                {language === "tr_TR" ? "Büyüktür" : "Greater Than"}
              </Option>
            )}
            {dataType === ValueType.Number && (
              <Option value={NumberFilterTypes.GreaterThanOrEqual}>
                {language === "tr_TR" ? "Büyük Eşittir" : "Greater Than or Equal"}
              </Option>
            )}
            {dataType === ValueType.Number && (
              <Option value={NumberFilterTypes.LessThan}>{language === "tr_TR" ? "Küçüktür" : "Less Than"}</Option>
            )}
            {dataType === ValueType.Number && (
              <Option value={NumberFilterTypes.LessThanOrEqual}>
                {language === "tr_TR" ? "Küçük Eşittir" : "Less Than or Equal"}
              </Option>
            )}
            {dataType === ValueType.Number && (
              <Option value={NumberFilterTypes.Blank}>{language === "tr_TR" ? "Boş" : "Blank"}</Option>
            )}
            {dataType === ValueType.Number && (
              <Option value={NumberFilterTypes.NotBlank}>{language === "tr_TR" ? "Boş Değildir" : "Not Blank"}</Option>
            )}
            {dataType === ValueType.Date && (
              <Option value={DateFilterTypes.After}>{language === "tr_TR" ? "Tarihinden Sonra" : "After Date"}</Option>
            )}
            {dataType === ValueType.Date && (
              <Option value={DateFilterTypes.Before}>{language === "tr_TR" ? "Tarihinden Önce" : "Before Date"}</Option>
            )}
            {dataType === ValueType.Date && (
              <Option value={DateFilterTypes.Between}>
                {language === "tr_TR" ? "Tarihleri Arasında" : "Between Dates"}
              </Option>
            )}
            {dataType === ValueType.Date && (
              <Option value={DateFilterTypes.Equals}>{language === "tr_TR" ? "Eşittir" : "Equals"}</Option>
            )}
            {dataType === ValueType.Date && (
              <Option value={DateFilterTypes.DoesNotEqual}>
                {language === "tr_TR" ? "Eşit Değildir" : "Does Not Equal"}
              </Option>
            )}
            {dataType === ValueType.Date && (
              <Option value={DateFilterTypes.Blank}>{language === "tr_TR" ? "Boş" : "Blank"}</Option>
            )}
            {dataType === ValueType.Date && (
              <Option value={DateFilterTypes.NotBlank}>{language === "tr_TR" ? "Boş Değildir" : "Not Blank"}</Option>
            )}
            {dataType === ValueType.Boolean && (
              <Option value={BooleanFilterTypes.Equals}>{language === "tr_TR" ? "Eşittir" : "Equals"}</Option>
            )}
            {dataType === ValueType.Boolean && (
              <Option value={BooleanFilterTypes.DoesNotEqual}>
                {language === "tr_TR" ? "Eşit Değildir" : "Does Not Equal"}
              </Option>
            )}
            {dataType === ValueType.Boolean && (
              <Option value={BooleanFilterTypes.Blank}>{language === "tr_TR" ? "Boş" : "Blank"}</Option>
            )}
            {dataType === ValueType.Boolean && (
              <Option value={BooleanFilterTypes.NotBlank}>{language === "tr_TR" ? "Boş Değildir" : "Not Blank"}</Option>
            )}
          </Select>

          {isString(secondSearchType) && !isSecondBlank && !isSecondNotBlank && (
            <Input
              placeholder={language === "tr_TR" ? "Filtrele..." : "Filter..."}
              value={secondSearchValue}
              onChange={(e) => handleSecondSearchValueChange(e)}
              style={{ width: "100%", marginBottom: 8, display: "block" }}
              readOnly={isDesignTime}
            />
          )}
          {isNumber(secondSearchType) && !isSecondBlank && !isSecondNotBlank && (
            <InputNumber
              placeholder={language === "tr_TR" ? "Filtrele..." : "Filter..."}
              value={secondSearchValue}
              onChange={(e) => handleSecondSearchValueChange(e)}
              style={{ width: "100%", marginBottom: 8, display: "block" }}
              readOnly={isDesignTime}
            />
          )}
          {secondSearchType === NumberFilterTypes.Between && (
            <InputNumber
              placeholder={language === "tr_TR" ? "İkinci değer..." : "Second value..."}
              value={secondSearchValue2}
              onChange={(e) => handleSecondSearchValueChange(e, true)}
              style={{ width: "100%", marginBottom: 8, display: "block" }}
              readOnly={isDesignTime}
            />
          )}
          {isDate(secondSearchType) && !isSecondBlank && !isSecondNotBlank && (
            <div style={{ width: "100%", marginBottom: 8 }}>
              <KDatePicker
                format={dateSearchFormat}
                displayType={
                  (typeof secondSearchValue === "string" &&
                    (secondSearchValue.includes(":") || secondSearchValue.includes("T"))) ||
                  (dateSearchFormat &&
                    (dateSearchFormat.includes("T") ||
                      dateSearchFormat.includes("HH:mm:ss") ||
                      dateSearchFormat.includes("HH:mm") ||
                      dateSearchFormat.includes("mm:ss")))
                    ? ("datetime" as any)
                    : ("date" as any)
                }
                placeholder={language === "tr_TR" ? "Filtrele..." : "Filter..."}
                value={secondSearchValue}
                isTableSearch
                onChange={(val) => handleSecondSearchValueChange(val)}
                disabled={isDesignTime}
                returnDisplayValue
              />
            </div>
          )}
          {secondSearchType === DateFilterTypes.Between && (
            <div style={{ width: "100%", marginBottom: 8 }}>
              <KDatePicker
                format={dateSearchFormat}
                displayType={
                  (typeof secondSearchValue2 === "string" &&
                    (secondSearchValue2.includes(":") || secondSearchValue2.includes("T"))) ||
                  (dateSearchFormat &&
                    (dateSearchFormat.includes("T") ||
                      dateSearchFormat.includes("HH:mm:ss") ||
                      dateSearchFormat.includes("HH:mm") ||
                      dateSearchFormat.includes("mm:ss")))
                    ? ("datetime" as any)
                    : ("date" as any)
                }
                placeholder={language === "tr_TR" ? "İkinci değer..." : "Second value..."}
                value={secondSearchValue2}
                isTableSearch
                onChange={(val) => handleSecondSearchValueChange(val, true)}
                returnDisplayValue
              />
            </div>
          )}
          {isBoolean(secondSearchType) && !isSecondBlank && !isSecondNotBlank && (
            <TriStateSwitch
              value={secondSearchValue || null}
              onChange={(value) => {
                setSecondSearchValue(value);
                setSelectedKeys({
                  value: searchValue,
                  value2: searchValue2,
                  type: searchType,
                  columnId,
                  logicalOperator,
                  secondType: secondSearchType,
                  secondValue: value,
                  secondValue2: secondSearchValue2
                });
              }}
              language={language}
              source={"FilterDropdown"}
            />
          )}
        </>
      )}

      <Space style={{ marginTop: 8 }}>
        <Button type="primary" onClick={handleSearch} size="small" style={{ width: 90 }}>
          {language === "tr_TR" ? "Filtrele" : "Filter"}
        </Button>
        <Button onClick={handleReset} size="small" style={{ width: 90 }}>
          {language === "tr_TR" ? "Sıfırla" : "Reset"}
        </Button>
      </Space>
    </div>
  );
};

export default CustomFilterDropdown;
